import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';

// Import layout components
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// Import page components
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import CreateOrderPage from './pages/CreateOrderPage';
import LeadsPage from './pages/LeadsPage';
import CreateLeadPage from './pages/CreateLeadPage';
import EditLeadPage from './pages/EditLeadPage';
import TechniciansPage from './pages/TechniciansPage';
import CreateTechnicianPage from './pages/CreateTechnicianPage';
import EditTechnicianPage from './pages/EditTechnicianPage';
import ProductsPage from './pages/ProductsPage';
import ProductGridPage from './pages/ProductGridPage';
import CreateProductPage from './pages/CreateProductPage';
import EditProductPage from './pages/EditProductPage';
import ContentCreatorPage from './pages/ContentCreatorPage';
import ContestPage from './pages/ContestPage';
import ExpensePage from './pages/ExpensePage';
import PaymentsPage from './pages/PaymentsPage';
import UsersPage from './pages/UsersPage';
import InventoryPage from './pages/InventoryPage';
import TechnicianStockPage from './pages/TechnicianStockPage';
import BlogsPage from './pages/BlogsPage';
import BlogFormPage from './pages/BlogFormPage';
import ReplacementsPage from './pages/ReplacementsPage';
import PlansPage from './pages/PlansPage';

// Default redirect based on role
const DefaultRedirect: React.FC = () => {
  try {
    const roles: string[] = JSON.parse(localStorage.getItem('user') || '{}').roles || [];
    const isTelecaller = roles.includes('Telecaller') && !roles.includes('Admin');
    return <Navigate to={isTelecaller ? '/orders' : '/dashboard'} replace />;
  } catch {
    return <Navigate to="/dashboard" replace />;
  }
};

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
          />
          
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DefaultRedirect />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/create" element={<CreateOrderPage />} />
            <Route path="orders/edit/:id" element={<CreateOrderPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/create" element={<CreateLeadPage />} />
            <Route path="leads/edit/:id" element={<EditLeadPage />} />
            <Route path="replacements" element={<ReplacementsPage />} />
            <Route path="technicians" element={<TechniciansPage />} />
            <Route path="technicians/create" element={<CreateTechnicianPage />} />
            <Route path="technicians/edit/:id" element={<EditTechnicianPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/grid" element={<ProductGridPage />} />
            <Route path="products/create" element={<CreateProductPage />} />
            <Route path="products/edit/:id" element={<EditProductPage />} />
            <Route path="content-creator" element={<ContentCreatorPage />} />
            <Route path="contests" element={<ContestPage />} />
            <Route path="expenses" element={<ExpensePage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="technician-stock" element={<TechnicianStockPage />} />
            <Route path="blogs" element={<BlogsPage />} />
            <Route path="blogs/create" element={<BlogFormPage />} />
            <Route path="blogs/edit/:id" element={<BlogFormPage />} />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App; 
